import React, { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sprite, SparkleBurst, PromptCapsule, JuicyMeter, CaptionChip, AmbientLayer } from "./fx";
import { HandIcon, MoonIcon } from "../ui-icons";

// PetSoothe — stroke a sleeping companion softly back and forth to fill a calm
// meter; sleepy z's and hearts drift up. Faithful port of iOS CityHelperPetView.

const STROKE_DISTANCE = 70;

const PetSoothe = ({ payload, sounds, onDone }) => {
  const target = payload.strokes || 6;
  const doneRef = useRef(false);
  const strokesRef = useRef(0);
  const accumRef = useRef(0);
  const lastXRef = useRef(null);
  const draggingRef = useRef(false);
  const seqRef = useRef(0);

  const [strokes, setStrokes] = useState(0);
  const [tilt, setTilt] = useState(false);
  const [drifts, setDrifts] = useState([]);
  const [sparkle, setSparkle] = useState(0);
  const [done, setDone] = useState(false);

  const progress = strokes / target;

  const countStroke = useCallback(() => {
    strokesRef.current += 1;
    const n = strokesRef.current;
    setStrokes(n);
    sounds.playPop(0.35 + (n / target) * 0.2);
    seqRef.current += 1;
    const id = seqRef.current;
    const item = { id, x: (Math.random() - 0.5) * 68, heart: n % 3 === 0 };
    setDrifts((prev) => [...prev, item]);
    window.setTimeout(() => setDrifts((prev) => prev.filter((d) => d.id !== id)), 1600);
    if (n >= target && !doneRef.current) {
      doneRef.current = true;
      setDone(true);
      sounds.playPop(0.3);
      sounds.playSparkle();
      sounds.speakSoothing("Purr… fast asleep.");
      setSparkle((s) => s + 1);
      window.setTimeout(() => onDone(), 1500);
    }
  }, [onDone, sounds, target]);

  const handleDown = useCallback((e) => {
    if (done) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    draggingRef.current = true;
    lastXRef.current = e.clientX;
  }, [done]);

  const handleMove = useCallback(
    (e) => {
      if (!draggingRef.current || done) return;
      const x = e.clientX;
      const last = lastXRef.current;
      if (last != null) {
        accumRef.current += Math.abs(x - last);
        setTilt(x > last);
        if (accumRef.current >= STROKE_DISTANCE) {
          accumRef.current = 0;
          countStroke();
        }
      }
      lastXRef.current = x;
    },
    [countStroke, done]
  );

  const handleUp = useCallback(() => {
    draggingRef.current = false;
    lastXRef.current = null;
  }, []);

  return (
    <div className="ch-activity-body ch-pet">
      <PromptCapsule text={payload.prompt} sounds={sounds} />

      <div
        className="ch-pet-stage"
        onPointerDown={handleDown}
        onPointerMove={handleMove}
        onPointerUp={handleUp}
        onPointerCancel={handleUp}
        style={{ touchAction: "none" }}
      >
        <span className="ch-pet-night" aria-hidden="true" />
        <AmbientLayer variant="night" />
        <span className="ch-pet-moon" aria-hidden="true"><MoonIcon size={44} /></span>
        <span className="ch-pet-glow" />

        {drifts.map((item) => (
          <motion.span
            key={item.id}
            className="ch-pet-drift"
            style={{ left: `calc(50% + ${item.x}px)` }}
            initial={{ bottom: "38%", opacity: 0.9, scale: 0.7 }}
            animate={{ bottom: "78%", opacity: 0, scale: 1.2, x: item.x * 0.5 }}
            transition={{ duration: 1.6, ease: "easeOut" }}
          >
            {item.heart ? (
              <span className="ch-pet-heart">♥</span>
            ) : (
              <span className="ch-pet-z">z</span>
            )}
          </motion.span>
        ))}

        <motion.div
          className="ch-pet-companion"
          animate={{ rotate: tilt ? 3 : -3, scaleY: done ? 1 : [0.99, 1.03] }}
          transition={{ rotate: { duration: 0.25 }, scaleY: { duration: 2.2, repeat: Infinity, repeatType: "reverse" } }}
        >
          <Sprite name={payload.companionAsset} />
          <SparkleBurst trigger={sparkle} reach={120} style={{ left: "50%", top: "10%" }} />
        </motion.div>

        {strokes === 0 && !done && (
          <motion.span
            className="ch-pet-hand"
            animate={{ x: [-40, 40] }}
            transition={{ duration: 1.1, repeat: Infinity, repeatType: "reverse" }}
            aria-hidden="true"
          >
            <HandIcon size={34} />
          </motion.span>
        )}
      </div>

      <JuicyMeter progress={progress} tint="#8ca6ff" icon={<MoonIcon size={22} />} full={done} />

      <CaptionChip tone={done ? "done" : strokes > 0 ? "active" : ""}>
        {done ? "Sweet dreams…" : "Stroke gently…"}
      </CaptionChip>
    </div>
  );
};

export default PetSoothe;
